import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { calcFsMaxWin, removeBtcFromTerms } from '@/modules/promo';
import { useFsCountFromQuery } from '@/modules/promo/hooks/useFsCountFromQuery';
import {
  MAX_WIN_SENTINEL,
  useMaxWinFromQuery,
} from '@/modules/promo/hooks/useMaxWinFromQuery';
import { useValidDate } from '@/modules/promo/hooks/useValidDateFromQuery';
import { ScTermsAndConditionsList } from './styled';

interface ITermsAndConditionsListProps {
  translation: string;
  validDate?: string;
  defaultFsCount?: number;
}

export const TermsAndConditionsList = ({
  translation,
  validDate,
  defaultFsCount,
}: ITermsAndConditionsListProps) => {
  const { t } = useTranslation([translation, 'lpAssets']);

  const validDateFromQuery = useValidDate(validDate);
  const fsCount = useFsCountFromQuery(defaultFsCount);
  // BTC amount in the terms copy is static and only accurate for the default fs count
  const isFsCountOverridden =
    defaultFsCount !== undefined && fsCount !== defaultFsCount;

  const maxWinMultiplier = useMaxWinFromQuery();
  const { maxWin, maxWinNok } = calcFsMaxWin(fsCount);

  const rules = useMemo(() => {
    const termsKeyProps = {
      returnObjects: true,
      ...(validDateFromQuery ? { date: validDateFromQuery } : {}),
      maxWin: maxWinMultiplier ? MAX_WIN_SENTINEL : maxWin,
      maxWinNok: maxWinMultiplier ? MAX_WIN_SENTINEL : maxWinNok,
    };

    const termsRules = removeBtcFromTerms(
      Object.values(t('termsContent', termsKeyProps)),
      isFsCountOverridden
    );

    if (!maxWinMultiplier) return termsRules;

    return termsRules.map((rule) =>
      typeof rule === 'string' && rule.includes(MAX_WIN_SENTINEL)
        ? t('lpAssets:maxWin.multiplierAmount', {
            multiplier: maxWinMultiplier,
          })
        : rule
    );
  }, [
    t,
    validDateFromQuery,
    maxWin,
    maxWinNok,
    maxWinMultiplier,
    isFsCountOverridden,
  ]);

  return (
    <ScTermsAndConditionsList>
      <div className="terms">
        <div className="terms-content">
          <ol>
            {rules.map((rule, index) => (
              <li key={index}>{rule as string}</li>
            ))}
          </ol>
        </div>
      </div>
    </ScTermsAndConditionsList>
  );
};
